import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { bindAction, type IWizardStep } from "interfaces";
import { connect, type ConnectedProps } from "react-redux";
import { GroupTemplatePacks } from "../Details/Packs/GroupTemplatePacks";
import { fetchAvailablePacks } from "../state/thunks/TemplateAvailablePacksThunk";
import { type AvailablePacksRequest } from "../types/models";
import { reset, resetGrid, setPacksToAdd } from "../state/TemplateAvailablePacksSlice";
import { useEffect, useMemo } from "react";
import { type PackMap } from "components/assignmentModals/groupTemplatePacksConfirmationModal/types";
import { noop } from "lodash";
import { Button } from "components/buttons/button/Button";
import { fetchAddOns } from "features/Accounts/state/thunks/addOnsThunk";
import { RouteNames } from "enums";
import { useNavigate } from "react-router";

interface Props extends IWizardStep, PropsFromRedux {
  packMap: PackMap;
}

export const CreatePacks = (props: Props) => {
  const {
    groupId,
    items,
    itemsCount,
    isLoading,
    isAllDataLoaded,
    selectedIds,
    setSelectedIds,
    getData,
    reset,
    resetGridItems,
    acceptHandlers,
    packMap,
    fetchAddOns,
    accountId,
  } = props;
  const navigate = useNavigate();

  useEffect(() => {
    acceptHandlers?.({
      onClose: reset,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAddOns(accountId);
  }, [fetchAddOns, accountId]);

  const loadPacks = (skip: number, searchTerm: string, orderBy: string) => {
    getData({ templateGroupId: groupId, skip, searchTerm, orderBy });
  };

  const hasNoPacks = itemsCount === 0 || items.length === 0;

  const actionButton = useMemo(() => {
    if (hasNoPacks) {
      return (
        <Button className={"temp"} primary onClick={() => navigate(`/${RouteNames.licensingPacks}/create`)}>
          Create Pack
        </Button>
      );
    }
    return null;
  }, [hasNoPacks, navigate]);

  return (
    <GroupTemplatePacks
      templateId={groupId}
      items={items}
      itemsCount={itemsCount}
      isLoading={isLoading}
      isAllDataLoaded={isAllDataLoaded}
      getGridData={loadPacks}
      getListData={noop}
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      resetGrid={resetGridItems}
      packMap={packMap}
      isCreate
      actionButton={actionButton}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    groupId: state.people.groupTemplate.base.groupId,
    items: state.people.groupTemplate.availablePacks.items,
    itemsCount: state.people.groupTemplate.availablePacks.itemsCount,
    isLoading: state.people.groupTemplate.availablePacks.isLoading,
    isAllDataLoaded: state.people.groupTemplate.availablePacks.areAllLoaded,
    selectedIds: state.people.groupTemplate.availablePacks.packsToAdd,
    addOns: state.accounts.addOns.items,
    accountId: state.userProfile.accountId,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    setSelectedIds: (ids: number[]) => dispatch(setPacksToAdd(ids)),
    getData: (request: AvailablePacksRequest) => dispatch(fetchAvailablePacks(request)),
    reset: () => dispatch(reset()),
    resetGridItems: () => dispatch(resetGrid()),
    fetchAddOns: bindAction(fetchAddOns, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(CreatePacks);
export default ConnectedComponent;
